/**
 * RAS / ReportRunner — parameter form + generate button for a selected template.
 *
 * /v3/audit-reports/* routes are PLANNED. Form is fully operational for
 * design review; submit button renders a friendly "coming soon" dialog.
 */

import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { ChevronLeft } from '@retail-ops/panel-shell/fi';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Pill } from '@/components/primitives';
import { RAS_TEMPLATES } from './components/TemplatePicker';

type Delivery = 'download' | 'teams' | 'secure-portal';
type Format = 'PDF' | 'CSV' | 'XML';

export function ReportRunner(): JSX.Element {
  const { templateCode } = useParams();
  const apiClient = useApiClient();
  const { user } = useAuth();
  const template = useMemo(
    () => RAS_TEMPLATES.find((t) => t.code === templateCode) ?? null,
    [templateCode],
  );

  const [startDate, setStartDate] = useState<string>(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
  );
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [branchFilter, setBranchFilter] = useState<string>('');
  const [productFilter, setProductFilter] = useState<string>('');
  const [redact, setRedact] = useState<boolean>(true);
  const [format, setFormat] = useState<Format>(template?.formats[0] ?? 'PDF');
  const [delivery, setDelivery] = useState<Delivery>('download');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'queued' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    if (!template) {
      setError('Unknown template code.');
      return;
    }
    setStatus('submitting');
    setError(null);
    try {
      // TODO: replace with apiClient.retail.auditReports.generate(...) once shipped.
      // For now, write a SessionEvent so the request is captured, then surface
      // the "coming soon" state.
      void apiClient.retail.events
        .write({
          eventType: 'audit-report-requested',
          eventCategory: 'compliance',
          severity: 'info',
          actor: user?.upn ?? 'unknown',
          payloadJson: JSON.stringify({
            templateCode: template.code,
            startDate,
            endDate,
            branchFilter,
            productFilter,
            redact,
            format,
            delivery,
          }),
        })
        .catch(() => undefined);
      setStatus('queued');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Failed to queue request');
    }
  };

  if (!template) {
    return (
      <div>
        <Link to="/ras" style={backLinkStyle}>
          <ChevronLeft size={14} /> Back to RAS
        </Link>
        <div
          style={{
            padding: 'var(--sp-7)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
          }}
        >
          Unknown template code: <code>{templateCode ?? '—'}</code>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Link to="/ras" style={backLinkStyle}>
        <ChevronLeft size={14} /> Back to RAS
      </Link>
      <header style={{ marginBottom: 'var(--sp-6)' }}>
        <h1 style={{ margin: 0 }}>
          {template.label} <span style={{ fontSize: 12, color: 'var(--ink-500)' }}>· {template.code}</span>
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-600)' }}>{template.blurb}</p>
      </header>

      <section
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--surface-border)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-7)',
          marginBottom: 'var(--sp-6)',
          display: 'grid',
          gap: 'var(--sp-5)',
          maxWidth: 720,
        }}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-5)' }}>
          <Field label="Start date" type="date" value={startDate} onChange={setStartDate} />
          <Field label="End date" type="date" value={endDate} onChange={setEndDate} />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-5)' }}>
          <Field label="Branch (optional)" value={branchFilter} onChange={setBranchFilter} />
          <Field label="Product (optional)" value={productFilter} onChange={setProductFilter} />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', fontSize: 13 }}>
          <input type="checkbox" checked={redact} onChange={(e) => setRedact(e.target.checked)} />
          Redact PII (default on — CTR filings require full SSN; disable only with BSA sign-off).
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--sp-5)' }}>
          <div>
            <label htmlFor="ras-format" style={fieldLabelStyle}>
              Output format
            </label>
            <select
              id="ras-format"
              value={format}
              onChange={(e) => setFormat(e.target.value as Format)}
              style={selectStyle}
            >
              {template.formats.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="ras-delivery" style={fieldLabelStyle}>
              Delivery
            </label>
            <select
              id="ras-delivery"
              value={delivery}
              onChange={(e) => setDelivery(e.target.value as Delivery)}
              style={selectStyle}
            >
              <option value="download">Download to my machine</option>
              <option value="teams">Teams DM / channel share</option>
              <option value="secure-portal">Secure Portal link (external auditor)</option>
            </select>
          </div>
        </div>
      </section>

      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
            marginBottom: 'var(--sp-5)',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'center' }}>
        <Button variant="primary" onClick={() => void submit()} disabled={status === 'submitting'}>
          Generate report
        </Button>
        {status === 'queued' && <Pill variant="ok">Queued (SessionEvent fired)</Pill>}
        <Pill variant="warn">Backend PLANNED: /v3/audit-reports/generate</Pill>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
}): JSX.Element {
  const id = `ras-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <div>
      <label htmlFor={id} style={fieldLabelStyle}>
        {label}
      </label>
      <input
        id={id}
        type={type ?? 'text'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={inputStyle}
      />
    </div>
  );
}

const fieldLabelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  textTransform: 'uppercase',
  color: 'var(--ink-500)',
  marginBottom: 'var(--sp-3)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--sp-4)',
  border: '1px solid var(--ink-300)',
  borderRadius: 'var(--r-md)',
  fontSize: 13,
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--surface-card)',
};

const backLinkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  fontSize: 12,
  color: 'var(--pcb-blue)',
  textDecoration: 'none',
  marginBottom: 'var(--sp-4)',
};
